import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import {
  createStyles,
  Theme,
  makeStyles,
  useTheme,
} from "@material-ui/core/styles";
import Divider from "@material-ui/core/Divider";
import { Typography } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import AnnouncementOutlinedIcon from "@material-ui/icons/AnnouncementOutlined";
import { useQuery, gql } from "@apollo/client";

import AppBar from "@Components/appBar/appBar";
import TagChips, { TagData } from "@Components/posts/tagChips";
import Section from "@Components/posts/section";
import TitleBlock from "@Components/posts/titleBlock";
import Footer from "@Components/common/footer";
import client from "@Config/graphCMSInit";
import SEO from "@Components/common/seo";
import Carousel, { ImageData } from "@Components/database/info/carousel";
import DisplayAd from "@Components/ads/displayAd";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mdHeader: { margin: `${theme.spacing(6)}px 0 ${theme.spacing(2)}px` },
    mdImage: { maxWidth: 600, margin: `${theme.spacing(2)}px auto` },
    gallery: {
      display: "flex",
      justifyContent: "center",
    },
    markdown: {
      textAlign: "justify",
      fontSize: "1.16rem",
      letterSpacing: "0.08rem",
      lineHeight: 1.96,
    },
    spacer: {
      marginTop: theme.spacing(6),
    },
  })
);

const GET_ARTICLE = gql`
  query Article($slug: String!) {
    article(where: { slug: $slug }) {
      simpleLayout
      title
      body
      description
      contentTags {
        name
        slug
        contentsWithTag {
          __typename
        }
      }
      gallery {
        mobile: url(
          transformation: {
            image: { resize: { width: 400, height: 400, fit: clip } }
            document: { output: { format: webp } }
          }
        )
        url(
          transformation: {
            image: { resize: { width: 1000, height: 1000, fit: clip } }
            document: { output: { format: webp } }
          }
        )
      }
    }
  }
`;

const GET_ARTICLES = gql`
  query {
    articles(first: 100) {
      slug
    }
  }
`;

export const getStaticProps = async ({ locale, params }: any) => {
  const cmsQuery = await client.query({
    query: GET_ARTICLE,
    variables: { slug: params.slug as string },
  });

  return {
    props: {
      data: cmsQuery.data,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
};

export async function getStaticPaths() {
  return new Promise((resolve, reject) => {
    client
      .query({
        query: GET_ARTICLES,
      })
      .then((result) => {
        const array = result.data.articles.map((item: any) => {
          return { params: { slug: item.slug } };
        });

        resolve({ paths: array, fallback: false });
      })
      .catch((error) => {
        reject(error);
      });
  });
}
interface StaticProps {
  data: {
    article: {
      simpleLayout: boolean;
      title: string;
      body: string;
      description: string;
      contentTags: TagData[];
      gallery: { mobile: string; url: string }[];
    };
  };
}

export default function Home({ data }: StaticProps) {
  const { t: t_common } = useTranslation("common");

  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [articleImageData, setArticleImageData] = useState<ImageData[]>([]);

  const header = (el: any) => {
    const { node } = el;
    const text = node.children[0]?.value;

    return (
      <div className={classes.mdHeader}>
        <Typography variant="h6">{text}</Typography>
      </div>
    );
  };

  const para = (el: any) => {
    const { node } = el;

    if (node.children[0]?.tagName === "img") {
      const image = node.children[0];
      const alt = image.properties.alt?.replace(/ *\{[^)]*\} */g, "");

      return (
        <figure className={classes.mdImage}>
          <Image
            objectFit="contain"
            src={image.properties.src}
            width={600}
            height={450}
            alt={alt}
            quality={100}
          />
          <Typography variant="body2" align="center" component="figcaption">
            {alt}
          </Typography>
        </figure>
      );
    }
    return <p>{el.children}</p>;
  };

  const MarkdownComponents: object = {
    h2: header,
    p: para,
  };

  useEffect(() => {
    if (data.article.gallery) {
      const Imgdata = data.article.gallery.map((image) => {
        return {
          src: mobile ? image.mobile : image.url,
          caption: data.article.title,
        } as ImageData;
      });

      setArticleImageData(Imgdata);
    }
  }, [mobile]);

  return (
    <React.Fragment>
      <SEO
        pageTitle={data ? data.article.title : "記事"}
        pageImg={
          data.article.gallery[0] ? data.article.gallery[0].url : undefined
        }
      />

      <AppBar bookmarkable>
        {data ? (
          <Section>
            <TitleBlock
              title={data.article.title}
              subtitle={data.article.description}
              breadcrumb={{
                parent: {
                  title: "記事",
                  icon: <AnnouncementOutlinedIcon />,
                  link: "/posts",
                },
                child: {
                  title: data.article.title,
                },
              }}
            />

            {articleImageData.length ? (
              <div className={classes.gallery}>
                <Carousel data={articleImageData} height={mobile ? 240 : 600} />
              </div>
            ) : null}

            <article>
              <ReactMarkdown
                className={classes.markdown}
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm]}
                components={
                  data.article.simpleLayout ? MarkdownComponents : undefined
                }
              >
                {data.article.body}
              </ReactMarkdown>
            </article>

            <DisplayAd variant="in_article" />

            <Divider />
            <div className={classes.spacer}></div>

            <TagChips header tags={data.article.contentTags} />
          </Section>
        ) : null}

        <Footer />
      </AppBar>
    </React.Fragment>
  );
}
